import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { Observable, OperatorFunction, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { EditableFieldBase } from '../editable-field-base';

// TYPEAHEAD SUPPORT INSTRUCTIONS 
// For TypeAhead support, include an observable to an array of objects that has the following properties.
// The objects may contain any other properties the application might require. The value of the control
// will contain the entire object, or just a string if no object is selected. 
//    * name (required) -- the display name to show in the pop-up and textbox when selected
//    * desc (optional) -- short descriptive text to display under the name
//    * img (optional) -- an image to display at the left of the option 

@Component({
    selector: 'array-builder',
    templateUrl: './array-builder.component.html',
    styleUrls: ['./array-builder.component.scss', '../editable-fields.scss'],
    standalone: false
})
export class ArrayBuilder extends EditableFieldBase implements OnInit, OnChanges {
  @Input() typeaheadList$: (query: string) => Observable<{ name }[]>;           // a callback method used to fetch the typeahead list
  @Input() placeholder: string = '';                                            // what to show when the value is empty
  @Input() requireTypeaheadSelection: boolean = false;                          //  For use in the typeahead input when the value MUST be selected from the list of options.
  @Input() typeaheadFilter: string;                                             //  Optional for typeaheads.  Allows you to refine the filter further beyond the text input value.
  @Output() optionSelectEmitter$: EventEmitter<any> = new EventEmitter<any>();  //  Emits when a typeahead option has been selected
  @Output() valueUpdatetEmitter$: EventEmitter<any> = new EventEmitter<any>();  //  Emits when the array has been updated

  //  IMPORTANT:  Only use this for small static lists of options like pronouns, not potentially big api calls like a person lookup.
  @Input() focusOpenTypeahead: boolean = false;                                 //  Allows the user to see a list of options without having to type anything into the search.
  @Input() arrayItemWidth: number;                                              //  If this is set the array items in the UI will have a specific width in pixels.
  @Input() isDisabled: boolean = false;                                         //  Temporary disable input and add.  Typically when we have a local list of data that would change the available options.
  @Input() isSearching: boolean = false;                                        //  Temporary disable add.  Used when we have an api fetch pulling data that would give additional options.

  // IMPORTANT: If we are using _displayValue instead of obj[prop] for the ngModel then use the updateValue input and point it to the obj[prop]
  // We are watching this for changes in ngOnChanges and it should be used if you are changing the bound object / property value in the ts code for your component.
  @Input() updateValue: string; 

  //  Both ViewChilds are used with the focusOpenTypeahead
  @ViewChild('ngbInstance') ngbInstance: NgbTypeahead;
  @ViewChild('ngbElement') ngbElement: ElementRef;

  protected _canAdd: boolean = false;
  protected _displayValue: string; 
  protected _actualValue: any[] = [];
  protected _typeaheadSelection: any;
  protected _hasUnsavedText: boolean = false;

  constructor() {
    super();
  }

  public ngOnInit() {
    super.ngOnInit();
    if (this.obj[this.prop]) {
      //  We may receive the current value in as a string array.  We need to re-shape
      //  the values so we can work with them consistently in the UI.
      const isStringArray = this.obj[this.prop].every(item => typeof item === 'string');

      if (!!isStringArray)
        this.obj[this.prop] = this.obj[this.prop].map(i => ({ name: i }));

      this._actualValue = this.obj[this.prop];
    }

    this.obj.validate();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    //  If the typeahead list is being re-fetched we are likely clearing the selections.
    //  If this happens we should make sure the _actualValue is re-set to the value of the
    //  input.  If it's null we need to re-set it to an empty array.
    if (changes['isDisabled']) {
      this._actualValue = this.obj[this.prop];

      if (this._actualValue == null)
        this._actualValue = [];
    }

    //  If a value is set using code instead of the input this keeps the UI updated accurately.
    if (changes['updateValue']) {
      this._actualValue = changes['updateValue']['currentValue'];
      this._displayValue = '';
    }
  }


  protected onEdit(newValue: string) {
    //  If we have an object we don't need to do any work, it is being handled by onTypeaheadOptionSelect().
    if (typeof newValue == 'object')
      return

    if (!!newValue) {
      this._typeaheadSelection = undefined;
      this._displayValue = newValue;
    }

    this.checkAdd();
  }

  //  Emits when an option has been selected from a typeahead dropdown.  Useful if you need to trigger an
  //  action when something is selected vs when something is typed into the input.
  protected onTypeaheadOptionSelect(value: any) {
    if (!value)
      return;

    if (typeof value == 'object') {
      this._typeaheadSelection = value;
      this.optionSelectEmitter$.emit(value);
      this.checkAdd();
      this.addValue();

      //  Defer clearing until ng-bootstrap finishes writing otherwise the _.displayValue gets stuck
      //  in the input until you click out of it.
      setTimeout(() => {
        this._displayValue = '';
      });
    }
  }

  // Input formatter for type-ahead inputs.  If you do not do this then typeahead objects
  //  will display in the input as [Object Object].
  public formatInput(item: any): string {
    let retVal = item;
    if (typeof item !== 'string') {
      retVal = item.name;
    }
    return retVal;
  }

  public openTypeahead(): void {
    this.ngbElement.nativeElement.dispatchEvent(new Event('input'));
    this.ngbElement.nativeElement.focus();
  }

  public search: OperatorFunction<string, readonly { name }[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(500),                  // we'll standardize the debounce time or consistency across apps
      distinctUntilChanged(),             // we only want changes
      switchMap(searchString => {
        if (this.typeaheadList$ != undefined) {
          if (this.focusOpenTypeahead || searchString.length >= 2) {
            let query = searchString;
            //  If additional filters are needed they should be included with the query before we send the
            //  query to the API.
            if (!!this.typeaheadFilter)
              query += `&${this.typeaheadFilter}`;

            return this.typeaheadList$(query)
          }
          else
              return of([])
        }
        else
          return of([])
      }
      ),
    );

  //  Used to verify if the user can actually add a new value to the array
  private checkAdd() {
    if ((!!this.requireTypeaheadSelection && !!this._typeaheadSelection?.name)
      || (!this.requireTypeaheadSelection && !!this._displayValue?.trim())) {
      this._canAdd = true;
    }
    else
      this._canAdd = false;
  }

  //  Give the option to emit the current display value when you leave the input.  Often used for
  //  triggering a UI element when text is entered but not added.
  public onBlur() {
    if (!this._canAdd) {
      this._displayValue = "";
      this.obj[this.prop] = this._actualValue;
    }

    this._hasUnsavedText = !!this._displayValue;
  }

  public addValue() {
    //  If we get into this method and the person is not able to add, do not do anything.
    if (!this._canAdd)
      return;

    //  If the value entered was not picked from typeahead results but we aren't requiring
    //  the selection to come from there, add it to _typeaheadSelection so we can uniformly return
    //  the results of this input.
    if (!this._typeaheadSelection && !this.requireTypeaheadSelection)
        this._typeaheadSelection = { name: this._displayValue };

    if (!!this._typeaheadSelection) {
      let displayValues = this._actualValue.map(v => v.name);

      if (!displayValues.includes(this._typeaheadSelection.name)) {
        this._actualValue.push(this._typeaheadSelection);
        this.obj.onChange();
      }
    }

    this._displayValue = "";
    this.obj[this.prop] = this._actualValue;
    this.obj.onChange();
    this.obj.validate();
    this._canAdd = false;
    this.valueUpdatetEmitter$.emit(this.obj[this.prop]);
    this.onBlur(); //  Trigger onBlur() again in case we're watching for the input to clear.
  }

  public removeValue(value) {
    this._actualValue = this._actualValue.filter(v => v.name != value.name);
    this.obj[this.prop] = this._actualValue;
    this.obj.onChange();
    this.obj.validate();
    this.valueUpdatetEmitter$.emit(this.obj[this.prop]);
  }

  //  Scrolls the selected result of the typeahead into view if the typeahead window is open.
  public navigateResults(event) {
    if (!!(event.target.nextElementSibling)
      && ( event.target.nextElementSibling.localName == 'ngb-typeahead-window' )) {
      let activeButton: Element = event.target.nextElementSibling.querySelector('.active');

      if (activeButton)
        activeButton.scrollIntoView({ behavior: "smooth", block: "nearest"});
    }
  }
}
